/**
 * Stay AI Subscription Widget Integration
 * Updates product price display and add-to-cart button when subscription options change.
 */
(function() {
  'use strict';

  const CONFIG = {
    productPriceSelector: 'product-price',
    addToCartPriceSelector: '[ref="addToCartPrice"], .add-to-cart-price',
    priceContainerSelector: '[ref="priceContainer"]',
    priceRegex: /\$[\d,]+\.?\d*/g
  };

  function parsePrice(priceString) {
    if (!priceString) return 0;
    return parseFloat(priceString.replace(/[^0-9.]/g, '')) || 0;
  }

  function extractPricesFromOption(element) {
    let price = null;
    let compareAtPrice = null;

    const radioValue = (element.value || '').toLowerCase();
    const radioId = (element.id || '').toLowerCase();
    const labelText = (element.closest('label')?.textContent || '').toLowerCase();

    const isOneTimeOption = radioValue.includes('onetime') || radioId.includes('onetime') || labelText.includes('one-time');
    const isSubscriptionOption = radioValue.includes('subscription') || radioId.includes('subscription') || labelText.includes('subscribe');

    const oneTimePriceElement = document.querySelector('[data-stayai-onetime-price]');
    const subscriptionPriceElement = document.querySelector('[data-stayai-subscription-price]');

    if (isOneTimeOption && oneTimePriceElement) {
      const priceMatch = oneTimePriceElement.textContent.trim().match(CONFIG.priceRegex);
      if (priceMatch) price = priceMatch[0];
    } else if (isSubscriptionOption && subscriptionPriceElement) {
      const allPrices = subscriptionPriceElement.textContent.trim().match(CONFIG.priceRegex) || [];

      if (allPrices.length >= 2) {
        const price1 = parsePrice(allPrices[0]);
        const price2 = parsePrice(allPrices[1]);
        if (price1 > price2) {
          compareAtPrice = allPrices[0];
          price = allPrices[1];
        } else {
          compareAtPrice = allPrices[1];
          price = allPrices[0];
        }
      } else if (allPrices.length === 1) {
        price = allPrices[0];
      }

      // Use one-time price as compare-at if not found
      if (!compareAtPrice && oneTimePriceElement) {
        const oneTimePriceMatch = oneTimePriceElement.textContent.trim().match(CONFIG.priceRegex);
        if (oneTimePriceMatch && price) {
          const oneTimeValue = parsePrice(oneTimePriceMatch[0]);
          const subscriptionValue = parsePrice(price);
          if (oneTimeValue > subscriptionValue) {
            compareAtPrice = oneTimePriceMatch[0];
          }
        }
      }
    }

    // Fallback: parse from container text
    if (!price) {
      const text = (element.closest('label, div')?.textContent || '');
      const prices = text.match(CONFIG.priceRegex) || [];
      if (prices.length >= 1) price = prices[prices.length - 1];
    }

    return { price, compareAtPrice };
  }

  function isSubscriptionOption(element) {
    const text = (element.closest('label, div')?.textContent || '').toLowerCase();
    const value = (element.value || '').toLowerCase();
    return text.includes('subscribe') || text.includes('subscription') || value.includes('subscription');
  }

  function updateProductPrice(price, compareAtPrice) {
    const productPriceElement = document.querySelector(CONFIG.productPriceSelector);
    if (!productPriceElement) return;

    if (typeof productPriceElement.updatePriceDisplay === 'function') {
      productPriceElement.updatePriceDisplay(price, compareAtPrice);
      return;
    }

    const priceContainer = productPriceElement.querySelector(CONFIG.priceContainerSelector);
    if (!priceContainer) {
      const priceSpan = productPriceElement.querySelector('.price');
      if (priceSpan) priceSpan.textContent = price;
      return;
    }

    if (compareAtPrice) {
      priceContainer.innerHTML = `
        <span role="group">
          <span class="visually-hidden">Regular price&nbsp;</span>
          <span class="compare-at-price">${compareAtPrice}</span>
        </span>
        <span role="group">
          <span class="visually-hidden">Sale price&nbsp;</span>
          <span class="price">${price}</span>
        </span>
      `;
    } else {
      priceContainer.innerHTML = `<span class="price">${price}</span>`;
    }
  }

  function updateAddToCartPrice(price) {
    const priceElements = document.querySelectorAll(CONFIG.addToCartPriceSelector);
    priceElements.forEach(el => el.textContent = price);
  }

  function handleOptionChange(selectedOption) {
    if (!selectedOption?.checked) return;

    const { price, compareAtPrice } = extractPricesFromOption(selectedOption);
    if (!price) return;

    const showCompareAt = isSubscriptionOption(selectedOption) && compareAtPrice;
    updateProductPrice(price, showCompareAt ? compareAtPrice : null);
    updateAddToCartPrice(price);
  }

  function findSubscriptionRadios() {
    return Array.from(document.querySelectorAll('input[type="radio"]')).filter(radio => {
      const text = (radio.closest('label, div')?.textContent || '').toLowerCase();
      return text.includes('purchase') || text.includes('subscribe') || text.includes('one-time');
    });
  }

  function processInitialSelection() {
    const radios = findSubscriptionRadios();
    const selectedRadio = radios.find(r => r.checked);
    if (selectedRadio) {
      handleOptionChange(selectedRadio);
    }
  }

  function setupListeners() {
    const radios = findSubscriptionRadios();

    radios.forEach(radio => {
      radio.addEventListener('change', () => handleOptionChange(radio));
    });

    // Click delegation for dynamically added elements
    document.addEventListener('click', (event) => {
      const radio = event.target.closest('input[type="radio"]');
      if (!radio) return;

      const text = (radio.closest('label, div')?.textContent || '').toLowerCase();
      if (text.includes('purchase') || text.includes('subscribe') || text.includes('one-time')) {
        setTimeout(() => handleOptionChange(radio), 10);
      }
    });

    // Process initially selected option
    processInitialSelection();
  }

  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setupListeners);
    } else {
      setupListeners();
    }

    // Delayed check for dynamically loaded Stay AI widget
    setTimeout(() => {
      setupListeners();
      processInitialSelection();
    }, 1000);
  }

  init();
})();
